import { useMemo, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Play } from "lucide-react";
import { Card } from "@/components/ui/card";
import CatchUpPanel from "@/components/home/CatchUpPanel";
import HeroBand from "@/components/home/HeroBand";
import InsightCards from "@/components/home/InsightCards";
import SegmentedTabs from "@/components/shared/SegmentedTabs";
import { useWorkMeta, WorkRow, MentionRow } from "@/components/home/WorkRow";
import { useMyWork } from "@/hooks/useMyWork";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useUnreadMentionsCount } from "@/hooks/useMentions";
import { isCompletionKind, type StatusKind } from "@/lib/statusKind";
import { useDropdownData } from "@/hooks/useDropdownData";
import { cn } from "@/lib/utils";
import type { BacklogItem, DropdownOption } from "@/types/backlog";
import type { Mention } from "@/hooks/useMentions";

const greetingForHour = (hour: number) => {
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
};

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** Rows a Home group shows before handing off to its /focus screen. */
const HOME_GROUP_CAP = 5;

/**
 * Home's panel ladder, in ms after mount.
 *
 * This page does not use the uniform `.page-stagger` rhythm (20ms then +60ms
 * for every child alike): it has a hero worth watching and panels of very
 * different weight, so each one lands on its own beat, ~200ms after the last.
 * The hero's own timings live in `.hero-band` (src/index.css) because they are
 * a fixed composition; these are here because the ladder has to close up when
 * a panel is absent, and only the page knows which panels it rendered.
 *
 * The ladder is two rungs shorter than it was: "Up for grabs" is now a tab on
 * the work card and "Done recently" folded into the celebration insight card,
 * so nothing between Catch Up and the insights takes a beat any more.
 */
const PANEL_STEP_MS = 200;
const WORK_DELAY_MS = 280;
const CATCH_UP_DELAY_MS = WORK_DELAY_MS + PANEL_STEP_MS;

/** Hero chips pop after the greeting has landed: 340ms, then +70ms each. */
const CHIP_DELAY_MS = 340;
const CHIP_STEP_MS = 70;

/**
 * The views of the My work panel — one card, swappable, not stacked.
 *
 * There was briefly a fourth, "Unassigned": the team queue, for work filed to
 * a team with nobody on it yet. It is gone because that state is not one this
 * workspace has — a ticket is assigned to someone when it is filed — so the
 * tab was a permanently empty rung on the one screen that has no room for one.
 */
type WorkTab = "todo" | "in-progress" | "needs-you";

/**
 * Left to right, the tabs read as the path a ticket takes — queued, in flight,
 * stuck — so the row of segments is a timeline rather than a ranking. The old
 * order put "Needs you" first because it is the loudest, which meant the
 * control's own layout re-argued the urgency the red pill already carries.
 *
 * This is presentation only. Which tab OPENS is decided below by
 * `defaultWorkTab`, off the urgent count and not off this array's order.
 */
const WORK_TABS: Array<{ key: WorkTab; label: string; kinds: StatusKind[] }> = [
  { key: "todo", label: "To do", kinds: ["todo"] },
  { key: "in-progress", label: "In progress", kinds: ["active"] },
  /* "Needs you now" while the card was narrower — the segments truncated it to
     "Needs you n…", and the "now" was the word doing the least. */
  { key: "needs-you", label: "Needs you", kinds: ["blocked"] },
];

/** The tab's own status colour, flashed by the slider as it lands there. */
const toneFor = (kinds: StatusKind[], statuses: DropdownOption[]): string | undefined =>
  statuses.find((s) => s.kind && kinds.includes(s.kind))?.color;

/** A WorkRow's height, and the hairline every row after the first adds. */
const WORK_ROW_HEIGHT_PX = 42;
const WORK_ROW_RULE_PX = 1;
/** The `p-1` the body carries, top and bottom. */
const MY_WORK_BODY_PADDING_PX = 8;

/**
 * The constant height of the My work card's body: the row cap at the row
 * height, plus the padding the region carries.
 *
 * The panel used to size itself to whichever tab was open, which meant that
 * switching from five rows to one pulled the insight cards and the closing
 * line up the page by 200px — on a screen designed to sit still. It now
 * reserves the space for a full tab always: a short tab shows its rows and
 * empty space beneath them, and nothing below the card ever moves.
 */
const MY_WORK_BODY_HEIGHT =
  HOME_GROUP_CAP * WORK_ROW_HEIGHT_PX + (HOME_GROUP_CAP - 1) * WORK_ROW_RULE_PX + MY_WORK_BODY_PADDING_PX;

/**
 * Home — the one personal surface.
 *
 * This absorbed the separate "My work" page (UX pass): the backlog is the
 * shared queue, and everything that answers "what should *I* do?" belongs
 * here, in one place, ordered by urgency rather than split across a dashboard
 * and a filtered table showing the same rows. Each group previews its top
 * rows; "View all" opens the group in full at /focus/:group.
 */
const Dashboard = () => {
  const navigate = useNavigate();
  const { data: userProfile } = useUserProfile();
  const { data: unreadCount = 0 } = useUnreadMentionsCount();
  const { live, buckets, unreadMentions, kindOf } = useMyWork();
  const meta = useWorkMeta();
  // The slider's arrival flash borrows each bucket's real status colour, the
  // same source the backlog rails read.
  const { data: dropdownData } = useDropdownData();
  const tabToneStatuses = dropdownData?.statuses ?? [];

  const needsYouCount = buckets.blocked.length + unreadMentions.length;
  const myActiveCount = buckets.blocked.length + buckets.active.length + buckets.todo.length;

  // Capped previews. Blocked outranks mentions inside the cap because a
  // blocked ticket is stopping someone else's work.
  const shownBlocked = buckets.blocked.slice(0, HOME_GROUP_CAP);
  const shownMentions = unreadMentions.slice(0, Math.max(1, HOME_GROUP_CAP - shownBlocked.length));
  const shownNeedsYou = shownBlocked.length + shownMentions.length;

  // The active My work view, until the user picks a tab themselves.
  const [workTabChoice, setWorkTabChoice] = useState<WorkTab | null>(null);
  const workTabCounts: Record<WorkTab, number> = {
    "needs-you": needsYouCount,
    "in-progress": buckets.active.length,
    todo: buckets.todo.length,
  };
  /**
   * The panel opens on "To do", always — the first tab, and the first question
   * of the day.
   *
   * It used to open on whichever tab was most urgent, which sounds helpful and
   * is not: the tab that opens moved with the data, so the same page showed a
   * different panel on two consecutive mornings and the reader had to work out
   * where they were before they could read anything. The urgency is already
   * said, twice — the red "Needs you" pill and its count are right there in the
   * control — and a standing signal the reader chooses to follow beats a
   * default that follows them.
   */
  const defaultWorkTab: WorkTab = "todo";
  const workTab: WorkTab = workTabChoice ?? defaultWorkTab;
  const workTabShown = workTab === "needs-you" ? shownNeedsYou : Math.min(HOME_GROUP_CAP, workTabCounts[workTab]);

  // "Pick up where you left off": the thing of mine that moved most recently.
  const resumeItem = useMemo(() => {
    const pool = buckets.active.length > 0 ? buckets.active : buckets.todo;
    return pool[0];
  }, [buckets]);

  // Cleared per week (whole workspace): completion-kind items bucketed by the
  // week they last moved. Framed as momentum, not judgment.
  const weeklyCleared = useMemo(() => {
    const week = new Array(7).fill(0) as number[];
    for (const item of live) {
      if (!isCompletionKind(kindOf(item.status_id))) continue;
      const weeksAgo = Math.floor((Date.now() - new Date(item.updated_at).getTime()) / WEEK_MS);
      if (weeksAgo >= 0 && weeksAgo < 7) week[6 - weeksAgo] += 1;
    }
    return week;
  }, [live, kindOf]);

  const clearedThisWeek = weeklyCleared[6];
  const bestPastWeek = Math.max(...weeklyCleared.slice(0, 6));
  const maxWeek = Math.max(1, ...weeklyCleared);
  const newThisWeek = useMemo(
    () => live.filter((i) => Date.now() - new Date(i.created_at).getTime() < WEEK_MS).length,
    [live],
  );
  const doneThisWeek = useMemo(
    () => buckets.done.filter((i) => Date.now() - new Date(i.updated_at).getTime() < WEEK_MS).length,
    [buckets.done],
  );

  const movingPct =
    myActiveCount === 0 ? 100 : Math.round(((myActiveCount - buckets.blocked.length) / myActiveCount) * 100);

  // Where the ladder has got to by the time the insight cards land, and then
  // the closing line, half a beat after the last of the three cards.
  const insightsDelay = CATCH_UP_DELAY_MS + PANEL_STEP_MS;
  const footerDelay = insightsDelay + 2 * PANEL_STEP_MS + PANEL_STEP_MS / 2;

  const firstName = userProfile?.first_name?.trim() || "there";
  const greeting = greetingForHour(new Date().getHours());

  const openItem = (item: BacklogItem) => navigate(`/item/${item.ticket_number}`);
  const openMention = (m: Mention) => navigate(`/item/${m.comment?.backlog_item?.ticket_number}`);

  const pulseLine = () => {
    const bits: string[] = [];
    if (unreadCount > 0) bits.push(`${unreadCount} mention${unreadCount === 1 ? "" : "s"} need a reply`);
    if (buckets.blocked[0]) bits.push(`#${buckets.blocked[0].ticket_number} has been blocked the longest`);
    if (bits.length === 0) return "All caught up — nothing is waiting on you.";
    return `${bits.join(", and ")}.`;
  };

  /**
   * "View all N →" — opens the tab's focused screen. Only rendered when the
   * cap hides rows. Every remaining tab key *is* its /focus/:group route now
   * that the one tab whose key differed from its URL is gone.
   */
  const ViewAll = ({ tab, total }: { tab: WorkTab; total: number }) => (
    <button type="button" onClick={() => navigate(`/focus/${tab}`)} className="textlink ml-auto text-xs">
      View all {total} <ArrowRight className="ux-arr h-3.5 w-3.5" />
    </button>
  );

  return (
    /* No `.page-stagger` here: Home lands panel by panel (see PANEL_STEP_MS)
       rather than on the uniform page rhythm.

       Home is a ONE-SCREEN page: hero, work + catch up, three insight cards
       and the closing line all fit a 1280×800 content area with the row cap
       (HOME_GROUP_CAP) full, so nothing that matters is below a fold. That is
       what the tightened paddings and gaps through here are buying.

       `.fits-one-screen` (index.css) then holds it to that promise: above the
       height the page is composed for there is nothing below the fold, so the
       page does not scroll at all — no wheel-nudge-and-bounce on a layout that
       already fits, and no 12px of dead travel under the closing line. Below
       that height the class does nothing and `overflow-auto` is the page
       again, because content that would clip has to stay reachable. */
    <div className="fits-one-screen flex h-full flex-col p-3 lg:px-6 lg:py-3">
      {/* hero greeting band — the one sanctioned gradient (XBG DS). The band
          reveals itself in two passes and tracks the cursor; see HeroBand. */}
      <HeroBand>
        <div className="relative flex flex-col gap-4 md:flex-row md:items-end">
          {/* `md:my-auto`: the greeting block is bottom-aligned with the chips
              (md:items-end) only while it is the taller of the two — which it
              is whenever the resume CTA renders, so with the button this row
              lays out exactly as before. Without the CTA the block is shorter
              than the chip row that then sets the band's height, and
              flex-end left it sitting off-centre in a band it no longer
              measures. Auto cross-axis margins outrank align-items and split
              whatever free space there is evenly, so the text centres itself
              in the taller case and changes nothing in the other. */}
          <div className="min-w-0 md:my-auto">
            <h1 className="hero-band__line text-[25px] font-extrabold tracking-tight">
              {greeting}, {firstName}
            </h1>
            <p
              className="hero-band__line mt-1 text-[13.5px] font-medium text-white/70"
              style={{ "--hero-delay": "260ms" } as CSSProperties}
            >
              {pulseLine()}
            </p>
            {resumeItem && (
              <button
                type="button"
                onClick={() => openItem(resumeItem)}
                className="hero-band__cta press-pop mt-3 inline-flex h-9 items-center gap-2 rounded-lg bg-[hsl(var(--sidebar-primary))] px-4 text-[13px] font-bold text-white shadow-[0_4px_14px_hsl(var(--sidebar-primary)/.4)] transition-all hover:-translate-y-px hover:brightness-110"
              >
                <Play className="h-3.5 w-3.5" />
                Pick up where you left off · #{resumeItem.ticket_number}
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2.5 md:ml-auto">
            {[
              { n: myActiveCount, label: "Yours active", hot: true },
              { n: unreadCount, label: "Mentions" },
              { n: buckets.blocked.length, label: "Blocked" },
            ].map((c, i) => (
              <div
                key={c.label}
                className="hero-band__chip min-w-[96px] rounded-xl border border-white/15 bg-white/10 px-3.5 py-2.5"
                style={{ "--hero-delay": `${CHIP_DELAY_MS + i * CHIP_STEP_MS}ms` } as CSSProperties}
              >
                <span className={cn("block text-xl font-extrabold", c.hot && "text-[hsl(var(--sidebar-primary))]")}>
                  {c.n}
                </span>
                <span className="text-[11px] font-semibold text-white/70">{c.label}</span>
              </div>
            ))}
          </div>
        </div>
      </HeroBand>

      {/* work + catch up + your week: ONE grid, three tracks.
          The lists on the left, notifications in view on the right instead of
          tucked behind the avatar menu — and the three insight cards below on
          the same tracks, so the Catch Up rail and the card beneath it are the
          same object width. That is the whole reason this is one grid and not
          two: the right rail used to be a fixed 340px while the cards were
          thirds of the row, so the two edges missed each other by whatever the
          viewport happened to make them.

          My work takes two tracks and Catch Up the third from xl up; below
          that both span the full row and the cards keep their own three
          across, which is the arrangement they always had.

          Everything STRETCHES (the grid default — nothing here is
          `self-start`): My work's body is a fixed height, so it sets the row,
          and Catch Up fills that height exactly rather than stopping short and
          leaving a band of page showing beside a full card. */}
      <div className="mt-3 grid gap-x-3 gap-y-3 md:grid-cols-3">
        <div className="min-w-0 md:col-span-3 xl:col-span-2">
          {/* my work — one panel, four views. The groups are answers to the
              same question ("what should I do?") at different levels of
              urgency, so they share a card and the user swaps between them
              instead of scrolling past stacked lists.

              The urgency stripe is always 3px tall and only changes *colour*:
              adding and removing the border re-flowed the card, so swapping
              tabs snapped the whole list up and down.

              Its calm colour is `--border`, the card's own outline, NOT
              transparent. A transparent 3px top edge is a hole in the outline:
              the other three sides draw and the top does not, which on the dark
              card reads as the box simply stopping along its top edge. Both
              states are real colours, so the swap is still a colour transition
              rather than a border appearing. */}
          <div className="panel-in" style={{ "--panel-delay": `${WORK_DELAY_MS}ms` } as CSSProperties}>
            <Card
              className={cn(
                "min-w-0 border-border border-t-[3px] transition-colors duration-[260ms]",
                workTab === "needs-you" && needsYouCount > 0 ? "border-t-[hsl(var(--ring))]" : "border-t-border",
              )}
            >
              {/* `min-h-[49px]` — its own natural height, pinned. This header
                  and Catch Up's are side by side on one grid row, and each
                  draws a rule under itself; two rules 6px apart across a gap
                  read as a misalignment rather than as two panels. Pinning
                  both is what keeps them level when either one's contents
                  change. See the matching note in CatchUpPanel, which carries
                  the 2px that this card's 3px top rail costs. */}
              <div className="flex min-h-[49px] flex-wrap items-center gap-2 border-b border-border/70 px-3 py-2">
                <SegmentedTabs
                  ariaLabel="My work view"
                  className="min-w-0 flex-1 sm:max-w-[520px]"
                  tabs={WORK_TABS.map((t) => ({
                    key: t.key,
                    label: t.label,
                    count: workTabCounts[t.key],
                    tone: toneFor(t.kinds, tabToneStatuses),
                  }))}
                  value={workTab}
                  onChange={setWorkTabChoice}
                />
                {workTabCounts[workTab] > workTabShown && <ViewAll tab={workTab} total={workTabCounts[workTab]} />}
              </div>
              {/* A constant height, not an animated one. The tabs hold
                different row counts, and a card that resizes to whichever is
                open drags the insight cards and the closing line up and down
                the page every time the user compares two tabs. The region is
                sized for a full tab (HOME_GROUP_CAP rows) and a short tab
                simply leaves the rest empty — the card is a fixed frame the
                lists are swapped inside. This is also what lets Home promise
                one screen with no scroll: its tallest state is its only
                state. */}
              <div className="overflow-y-auto p-1" style={{ height: MY_WORK_BODY_HEIGHT }}>
                {workTab === "needs-you" ? (
                  needsYouCount === 0 ? (
                    <p className="px-3 py-3 text-sm text-muted-foreground">Nothing is waiting on you — all clear.</p>
                  ) : (
                    <>
                      {shownBlocked.map((item) => (
                        <WorkRow key={item.id} item={item} meta={meta} onOpen={openItem} aging="blocked" />
                      ))}
                      {shownMentions.map((m) => (
                        <MentionRow key={m.id} mention={m} onOpen={openMention} />
                      ))}
                    </>
                  )
                ) : workTab === "in-progress" ? (
                  buckets.active.length === 0 ? (
                    <p className="px-3 py-3 text-sm text-muted-foreground">Nothing in flight.</p>
                  ) : (
                    buckets.active
                      .slice(0, HOME_GROUP_CAP)
                      .map((item) => <WorkRow key={item.id} item={item} meta={meta} onOpen={openItem} />)
                  )
                ) : buckets.todo.length === 0 ? (
                  <p className="px-3 py-3 text-sm text-muted-foreground">Nothing queued up.</p>
                ) : (
                  buckets.todo
                    .slice(0, HOME_GROUP_CAP)
                    .map((item) => <WorkRow key={item.id} item={item} meta={meta} onOpen={openItem} />)
                )}
              </div>
            </Card>
          </div>
        </div>

        <div
          // No `h-full` here: a percentage height on the grid ITEM resolves
          // against the grid container (auto), so it computes to `auto` and —
          // worse — an explicit height cancels `align-self: stretch`, which is
          // the thing actually doing the work. The item stretches; the Card
          // inside it takes `h-full` against that now-definite height.
          className="panel-in min-w-0 md:col-span-3 xl:col-span-1"
          style={{ "--panel-delay": `${CATCH_UP_DELAY_MS}ms` } as CSSProperties}
        >
          <CatchUpPanel />
        </div>

        {/* your week — only the numbers someone acts on, framed to feel good.
          The three cards carry their own mount reveal, idle loop and hover
          performance, so they live in their own component.

          The third card is also where finished work now lives. "Inbox zero on
          mentions" and a folded "Done recently" section were the same
          feel-good beat said twice on one screen, so the section went and the
          card kept its message and gained the link out to /focus/done. */}
        <InsightCards
          weeklyCleared={weeklyCleared}
          clearedThisWeek={clearedThisWeek}
          bestPastWeek={bestPastWeek}
          maxWeek={maxWeek}
          movingPct={movingPct}
          activeCount={myActiveCount}
          blockedCount={buckets.blocked.length}
          unreadCount={unreadCount}
          doneThisWeek={doneThisWeek}
          doneCount={buckets.done.length}
          onViewDone={() => navigate("/focus/done")}
          baseDelay={insightsDelay}
        />
      </div>

      {/* one sentence instead of six stat tiles.
          It used to carry a "View insights →" link on its right, which made a
          whole page reachable only through a footnote at the bottom of another
          one. Insights is a nav entry now (PrimaryNav, directly under Home), so
          the sentence is left to be what it always was — the week in a line. */}
      <div
        className="panel-in mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 px-1 pb-0.5 text-[12.5px] font-medium text-muted-foreground"
        style={{ "--panel-delay": `${footerDelay}ms` } as CSSProperties}
      >
        This week: <b className="text-foreground">{clearedThisWeek} done</b> ·{" "}
        <b className="text-foreground">{newThisWeek} new</b> · the team is{" "}
        <b className="text-foreground">{clearedThisWeek >= newThisWeek ? "keeping pace" : "taking on more"}</b>
      </div>
    </div>
  );
};

export default Dashboard;
